import { useEffect } from "react";

export const REPORTED_TIMEZONE_KEY = "yagni:reported-timezone";
export const TIMEZONE_MANUAL_OVERRIDE_KEY = "yagni:timezone-manual-override";

/**
 * Push the browser's current IANA timezone to the backend so agent prompts
 * and meeting briefs format absolute times in the user's zone instead of
 * silently falling back to UTC.
 *
 * Called once per session from `App`; debounced against localStorage so a
 * stable zone is a no-op after the first load. DST transitions flip the
 * offset but not the IANA name, so we do NOT need to re-post on every
 * load — the server renders with `Intl.DateTimeFormat` which re-computes
 * the offset at each format call.
 */
export function useReportTimezone(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    let tz: string | undefined;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return;
    }
    if (!tz) return;
    if (window.localStorage.getItem(TIMEZONE_MANUAL_OVERRIDE_KEY) === "true") return;

    const last = window.localStorage.getItem(REPORTED_TIMEZONE_KEY);
    if (last === tz) return;

    void fetch("/api/account/timezone", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ timezone: tz }),
    })
      .then((res) => {
        if (res.ok) {
          window.localStorage.setItem(REPORTED_TIMEZONE_KEY, tz!);
        }
      })
      .catch(() => {
        // Best-effort — a failed timezone report shouldn't surface to the
        // user or block the app. Next load will retry.
      });
  }, [enabled]);
}
